import React, { useEffect, useState } from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LogoUpim } from '../../components/ui-upim';
import {
  LayoutDashboard,
  Users,
  Building2,
  Calendar,
  MessageSquare,
  Settings,
  ImageIcon,
  FileText,
  CreditCard,
  Receipt,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronRight,
  QrCode,
  Video,
  Award,
  Ticket,
  MessageCircle,
  BarChart3,
  UserCircle,
  Shield,
  Bot,
  Phone,
  Play,
  UserCheck,
  Folder,
  BookOpen,
  Mail,
  FolderOpen,
  FileQuestion,
  Gift,
  Radio,
  Crown,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { orgAPI } from '../../services/api';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: BarChart3, label: 'Analytics', path: '/admin/analytics' },
  { icon: Crown, label: 'Estructura Nacional', path: '/admin/nacional' },
  { icon: Shield, label: 'Gobernanza', path: '/admin/governance' },
  { icon: Crown, label: 'Directores', path: '/admin/institucional' },
  { icon: Building2, label: 'Proveedores', path: '/admin/proveedores' },
  { icon: Bot, label: 'UPIM IA', path: '/admin/upim-ia' },
  { icon: Phone, label: 'Inbox WhatsApp', path: '/admin/omnichannel' },
  { icon: BookOpen, label: 'Academia', path: '/admin/academia' },
  { icon: FileQuestion, label: 'Exámenes', path: '/admin/examenes' },
  { icon: Award, label: 'Reconocimientos', path: '/admin/reconocimientos' },
  { icon: Gift, label: 'Badges', path: '/admin/badges' },
  { icon: Mail, label: 'Email Marketing', path: '/admin/email-marketing' },
  { icon: FolderOpen, label: 'Biblioteca Recursos', path: '/admin/biblioteca' },
  { icon: Play, label: 'Biblioteca Videos', path: '/admin/videos' },
  { icon: Radio, label: 'Lives / Streaming', path: '/admin/lives' },
  { icon: UserCheck, label: 'Ponentes', path: '/admin/ponentes' },
  { icon: Folder, label: 'Materiales', path: '/admin/materiales' },
  { icon: UserCircle, label: 'CRM', path: '/admin/crm' },
  { icon: Users, label: 'Afiliados', path: '/admin/afiliados' },
  { icon: Users, label: 'Afiliaciones', path: '/admin/afiliaciones' },
  { icon: RefreshCw, label: 'Renovaciones', path: '/admin/renovaciones' },
  { icon: UserCircle, label: 'CRM territorial', path: '/admin/crm-territorial' },
  { icon: Shield, label: 'Verificaciones', path: '/admin/verificaciones' },
  { icon: ImageIcon, label: 'Carruseles', path: '/admin/carruseles' },
  { icon: Calendar, label: 'Eventos', path: '/admin/eventos' },
  { icon: Video, label: 'Webinars', path: '/admin/webinars' },
  { icon: Award, label: 'Certificaciones', path: '/admin/certificaciones' },
  { icon: Ticket, label: 'Cupones', path: '/admin/cupones' },
  { icon: MessageCircle, label: 'Notificaciones', path: '/admin/notificaciones' },
  { icon: QrCode, label: 'Check-in', path: '/admin/checkin' },
  { icon: MessageSquare, label: 'Mensajes', path: '/admin/mensajes' },
  { icon: CreditCard, label: 'Pagos', path: '/admin/pagos' },
  { icon: Receipt, label: 'Facturación', path: '/facturacion/solicitudes' },
  { icon: Settings, label: 'Configuración', path: '/admin/configuracion' },
  { icon: Shield, label: 'Seguridad de la cuenta', path: '/cuenta/seguridad' },
];

const AdminLayout = ({ children }) => {
  const { user, logout, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accessContext, setAccessContext] = useState(null);
  const [accessLoading, setAccessLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    if (loading) return undefined;
    if (!isAuthenticated) {
      setAccessLoading(false);
      return undefined;
    }
    orgAPI.myContext()
      .then((context) => {
        if (mounted) setAccessContext(context);
      })
      .catch(() => {
        if (mounted) setAccessContext(null);
      })
      .finally(() => {
        if (mounted) setAccessLoading(false);
      });
    return () => { mounted = false; };
  }, [isAuthenticated, loading]);

  const isNational = !!accessContext?.control_total || accessContext?.accessible_scope === 'ALL';
  const canManageAffiliations = accessContext?.permissions?.includes('afiliado.ver');
  const permisos = accessContext?.permissions || [];
  const puedeFacturar = permisos.includes('factura.gestionar');
  // El CONTADOR es un rol nacional de MÍNIMO privilegio: sólo debe ver Facturación.
  const soloFiscal = !accessContext?.control_total && puedeFacturar
    && !permisos.includes('usuario.ver');
  const fiscalPaths = new Set(['/facturacion/solicitudes']);
  const sectionPaths = new Set(['/admin/afiliaciones', '/admin/renovaciones',
    '/admin/crm-territorial', '/cuenta/seguridad']);
  const territorialGated = new Set(['/admin/afiliaciones', '/admin/renovaciones',
    '/admin/crm-territorial']);
  let visibleNavItems;
  if (soloFiscal) {
    visibleNavItems = navItems.filter((item) => fiscalPaths.has(item.path)
      || item.path === '/cuenta/seguridad');
  } else if (isNational) {
    visibleNavItems = navItems.filter((item) => !fiscalPaths.has(item.path) || puedeFacturar);
  } else {
    visibleNavItems = navItems.filter((item) => (sectionPaths.has(item.path)
      && (!territorialGated.has(item.path) || canManageAffiliations))
      || (fiscalPaths.has(item.path) && puedeFacturar));
  }
  const roleLabel = soloFiscal
    ? 'Contaduría'
    : (isNational
      ? 'Administración Nacional'
      : (accessContext?.accessible_sections?.[0]?.name || 'Administración de Sección'));

  const handleLogout = () => {
    if (window.confirm('¿Estás seguro de que deseas cerrar sesión?')) {
      logout();
      window.location.href = '/login';
    }
  };

  if (loading || accessLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!accessContext || (!isNational && !(accessContext.roles || []).length)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (soloFiscal && location.pathname.startsWith('/admin')
      && location.pathname !== '/cuenta/seguridad') {
    return <Navigate to="/facturacion/solicitudes" replace />;
  }

  if (!isNational && location.pathname.startsWith('/admin/') &&
      !visibleNavItems.some((item) => item.path === location.pathname)) {
    return <Navigate to={canManageAffiliations ? '/admin/afiliaciones' : '/dashboard'} replace />;
  }

  if (!isNational && location.pathname === '/admin') {
    return <Navigate to={canManageAffiliations ? '/admin/afiliaciones' : '/dashboard'} replace />;
  }

  return (
    <div className="min-h-screen bg-black" data-testid="admin-shell">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 flex h-full w-64 flex-col border-r border-[var(--upim-borde)] bg-[var(--upim-superficie-1)] text-[var(--upim-texto)] transform transition-transform duration-200
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="h-[64px] flex items-center justify-between px-4 border-b border-[var(--upim-borde)] flex-shrink-0">
          <Link to="/admin" className="flex items-center gap-2.5" data-testid="admin-logo-enlace">
            <LogoUpim className="h-8" claseTexto="text-base" testId="admin-logo" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--upim-acento)]">Admin</span>
          </Link>
          <button
            className="lg:hidden text-[var(--upim-texto-3)] hover:text-[var(--upim-texto)]"
            onClick={() => setSidebarOpen(false)}
            aria-label="Cerrar menú"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1 pb-32">
          {visibleNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  relative flex items-center space-x-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer
                  ${isActive
                    ? 'bg-[var(--upim-acento-suave)] text-[var(--upim-texto)]'
                    : 'text-[var(--upim-texto-2)] hover:text-[var(--upim-texto)] hover:bg-[var(--upim-superficie-3)]'}
                `}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {isActive && <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-[var(--upim-acento)]" aria-hidden="true" />}
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Footer - Fixed at bottom */}
        <div className="flex-shrink-0 border-t border-[var(--upim-borde)] bg-[var(--upim-superficie-1)] p-4">
          <Link
            to="/"
            className="flex items-center space-x-3 rounded-xl px-3 py-3 text-[var(--upim-texto-2)] transition-colors hover:bg-[var(--upim-superficie-3)] hover:text-[var(--upim-texto)] cursor-pointer"
          >
            <ChevronRight className="h-5 w-5 rotate-180 flex-shrink-0" />
            <span className="text-sm font-medium">Volver al sitio</span>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-3 py-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors mt-2 cursor-pointer"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm font-medium">Cerrar sesión</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="upim-vidrio sticky top-0 z-30 flex h-16 items-center justify-between px-4 lg:px-6" data-testid="admin-barra-superior">
          <button
            className="lg:hidden p-2 text-gray-600 hover:text-gray-900"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex items-center space-x-4 ml-auto">
            <button className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
              <Bell className="h-5 w-5" />
              <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>
            </button>
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-gray-600">
                  {user?.name?.charAt(0) || 'A'}
                </span>
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-gray-900">{user?.name || 'Administrador'}</p>
                <p className="text-xs text-gray-500">{roleLabel}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
